import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { db } from "../firebase";

import {
  collection,
  getDocs,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import "./MatchList.css";

/*
=========================================================
MAIN COMPONENT
=========================================================
*/

export default function SpecialGames() {
  const navigate = useNavigate();

  const [matches, setMatches] = useState([]);
  const [visible, setVisible] = useState(4);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  /*
  =========================================================
  SETTINGS
  =========================================================
  */

  const TIMEZONE =
    "Africa/Dar_es_Salaam";

  const HIDE_BEFORE_MINUTES = 15;

  const HIDE_BEFORE_MS =
    HIDE_BEFORE_MINUTES *
    60 *
    1000;

  /*
  =========================================================
  QUANTITY SETTINGS
  =========================================================

  1100M = 1.1 Billion
  1900M = 1.9 Billion

  Quantity inaongezeka kadri kickoff
  inavyokaribia.

  HAIBADILISHI FIRESTORE.
  =========================================================
  */

  const QUANTITY_MIN_M = 1100;
  const QUANTITY_MAX_M = 1900;

  /*
  ---------------------------------------------------------
  PROGRESSION WINDOW
  ---------------------------------------------------------

  Quantity inaprogress ndani ya
  masaa 24 kabla ya kickoff.
  */

  const QUANTITY_PROGRESS_MS =
    24 * 60 * 60 * 1000;

  /*
  =========================================================
  LIVE CLOCK
  =========================================================
  */

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  /*
  =========================================================
  PARSE MATCH DATE
  =========================================================
  */

  const parseMatchDate = (match) => {
    if (!match) {
      return null;
    }

    /*
    =======================================================
    TIMESTAMP FIELDS
    =======================================================
    */

    const possibleDateFields = [
      match.startTimestamp,
      match.startTime,
      match.kickoff,
      match.matchDate,
    ];

    for (const field of possibleDateFields) {
      if (!field) {
        continue;
      }

      try {
        /*
        ---------------------------------------------------
        FIREBASE TIMESTAMP
        ---------------------------------------------------
        */

        if (
          typeof field === "object" &&
          typeof field.toDate === "function"
        ) {
          const result = field.toDate();

          if (!isNaN(result.getTime())) {
            return result;
          }
        }

        /*
        ---------------------------------------------------
        FIRESTORE SERIALIZED TIMESTAMP
        ---------------------------------------------------
        */

        if (
          typeof field === "object" &&
          typeof field.seconds === "number"
        ) {
          const result = new Date(
            field.seconds * 1000
          );

          if (!isNaN(result.getTime())) {
            return result;
          }
        }

        /*
        ---------------------------------------------------
        JAVASCRIPT DATE
        ---------------------------------------------------
        */

        if (field instanceof Date) {
          if (!isNaN(field.getTime())) {
            return field;
          }
        }

        /*
        ---------------------------------------------------
        STRING
        ---------------------------------------------------
        */

        const stringValue =
          String(field).trim();

        if (stringValue) {
          const result =
            new Date(stringValue);

          if (!isNaN(result.getTime())) {
            return result;
          }
        }
      } catch (error) {
        console.error(
          "START DATE PARSE ERROR:",
          error
        );
      }
    }

    /*
    =======================================================
    FIRESTORE DATE + TIME
    =======================================================
    */

    const dateValue =
      String(
        match.date || ""
      ).trim();

    const timeValue =
      String(
        match.time || "00:00"
      ).trim();

    if (!dateValue) {
      return null;
    }

    const timezone =
      String(
        match.timezone ||
          TIMEZONE
      ).trim();

    /*
    =======================================================
    YYYY-MM-DD
    =======================================================
    */

    const dateParts =
      dateValue.match(
        /^(\d{4})-(\d{2})-(\d{2})$/
      );

    if (dateParts) {
      const year =
        Number(dateParts[1]);

      const month =
        Number(dateParts[2]);

      const day =
        Number(dateParts[3]);

      /*
      -----------------------------------------------------
      HH:mm
      -----------------------------------------------------
      */

      const timeParts =
        timeValue.match(
          /^(\d{1,2}):(\d{2})/
        );

      const hours =
        timeParts
          ? Number(timeParts[1])
          : 0;

      const minutes =
        timeParts
          ? Number(timeParts[2])
          : 0;

      /*
      =====================================================
      BUILD DATE USING TIMEZONE
      =====================================================
      */

      try {
        const approximateUTC =
          new Date(
            Date.UTC(
              year,
              month - 1,
              day,
              hours,
              minutes,
              0,
              0
            )
          );

        const formatter =
          new Intl.DateTimeFormat(
            "en-US",
            {
              timeZone: timezone,

              timeZoneName:
                "longOffset",

              year: "numeric",

              month: "2-digit",

              day: "2-digit",

              hour: "2-digit",

              minute: "2-digit",

              second: "2-digit",

              hourCycle: "h23",
            }
          );

        const parts =
          formatter.formatToParts(
            approximateUTC
          );

        const offsetPart =
          parts.find(
            (part) =>
              part.type ===
              "timeZoneName"
          );

        let offsetMinutes = 0;

        if (
          offsetPart &&
          offsetPart.value
        ) {
          const offsetMatch =
            offsetPart.value.match(
              /GMT([+-])(\d{2})(?::?(\d{2}))?/
            );

          if (offsetMatch) {
            const sign =
              offsetMatch[1] ===
              "+"
                ? 1
                : -1;

            const offsetHours =
              Number(
                offsetMatch[2]
              );

            const offsetMins =
              Number(
                offsetMatch[3] ||
                  0
              );

            offsetMinutes =
              sign *
              (
                offsetHours *
                  60 +
                offsetMins
              );
          }
        }

        const finalDate =
          new Date(
            approximateUTC.getTime() -
              offsetMinutes *
                60 *
                1000
          );

        if (
          !isNaN(
            finalDate.getTime()
          )
        ) {
          return finalDate;
        }
      } catch (error) {
        console.error(
          "TIMEZONE DATE ERROR:",
          error
        );
      }

      /*
      =====================================================
      TANZANIA FALLBACK
      =====================================================
      */

      if (
        timezone ===
        "Africa/Dar_es_Salaam"
      ) {
        return new Date(
          Date.UTC(
            year,
            month - 1,
            day,
            hours - 3,
            minutes,
            0,
            0
          )
        );
      }
    }

    /*
    =======================================================
    GENERIC FALLBACK
    =======================================================
    */

    try {
      const combined =
        `${dateValue}T${timeValue}:00`;

      const result =
        new Date(combined);

      if (
        !isNaN(
          result.getTime()
        )
      ) {
        return result;
      }
    } catch (error) {
      console.error(
        "GENERIC DATE ERROR:",
        error
      );
    }

    return null;
  };

  /*
  =========================================================
  HASH STRING
  =========================================================

  Inafanya kila match iwe na
  starting/final quantity yake.
  =========================================================
  */

  const hashString = (value) => {
    const text =
      String(value || "");

    let hash = 0;

    for (
      let i = 0;
      i < text.length;
      i += 1
    ) {
      hash =
        (
          (hash << 5) -
          hash +
          text.charCodeAt(i)
        ) |
        0;
    }

    return Math.abs(hash);
  };

  /*
  =========================================================
  DYNAMIC QUANTITY
  =========================================================

  Quantity:

  - inaanzia 1.1B - 1.4B
  - inaongezeka kila dakika
  - inaelekea 1.75B - 1.9B
  - haizidi 1.9B
  - haishuki chini ya 1.1B
  - haijaandikwa Firestore
  =========================================================
  */

  const getDynamicQuantity = (
    match,
    currentTime
  ) => {
    const matchDate =
      parseMatchDate(match);

    if (!matchDate) {
      return QUANTITY_MIN_M;
    }

    /*
    =======================================================
    UNIQUE MATCH SEED
    =======================================================
    */

    const seedText =
      [
        match?.id,
        match?.A,
        match?.B,
        match?.date,
        match?.time,
      ]
        .filter(Boolean)
        .join("-");

    const seed =
      hashString(seedText);

    /*
    =======================================================
    START QUANTITY
    =======================================================

    1100M mpaka 1400M
    =======================================================
    */

    const startRange =
      300;

    const startQuantity =
      QUANTITY_MIN_M +
      (
        seed %
        (startRange + 1)
      );

    /*
    =======================================================
    FINAL QUANTITY
    =======================================================

    1750M mpaka 1900M
    =======================================================
    */

    const finalMin =
      1750;

    const finalRange =
      QUANTITY_MAX_M -
      finalMin;

    const finalQuantity =
      finalMin +
      (
        hashString(
          `${seed}-FINAL`
        ) %
        (finalRange + 1)
      );

    /*
    =======================================================
    TIME LEFT
    =======================================================
    */

    const matchTime =
      matchDate.getTime();

    const timeLeft =
      matchTime -
      currentTime;

    /*
    =======================================================
    ELAPSED TIME
    =======================================================
    */

    const elapsed =
      QUANTITY_PROGRESS_MS -
      timeLeft;

    /*
    =======================================================
    MINUTES ELAPSED
    =======================================================

    Muhimu:

    Quantity haiongezeki kila second.

    Inaongezeka baada ya dakika
    mpya kuingia.
    =======================================================
    */

    const elapsedMinutes =
      Math.floor(
        Math.max(
          0,
          elapsed
        ) /
          (60 * 1000)
      );

    /*
    =======================================================
    CONVERT BACK TO TIME
    =======================================================
    */

    const minuteElapsed =
      elapsedMinutes *
      60 *
      1000;

    /*
    =======================================================
    PROGRESS
    =======================================================
    */

    let progress =
      minuteElapsed /
      QUANTITY_PROGRESS_MS;

    progress =
      Math.max(
        0,
        Math.min(
          1,
          progress
        )
      );

    /*
    =======================================================
    CALCULATE QUANTITY
    =======================================================
    */

    let quantity =
      startQuantity +
      (
        finalQuantity -
        startQuantity
      ) *
        progress;

    /*
    =======================================================
    ROUND DOWN
    =======================================================
    */

    quantity =
      Math.floor(
        quantity
      );

    /*
    =======================================================
    HARD LIMITS
    =======================================================
    */

    quantity =
      Math.max(
        QUANTITY_MIN_M,
        quantity
      );

    quantity =
      Math.min(
        QUANTITY_MAX_M,
        quantity
      );

    return quantity;
  };

  /*
  =========================================================
  GET DATE KEY
  =========================================================
  */

  const getDateKey = (
    date,
    timezone = TIMEZONE
  ) => {
    if (!date) {
      return "";
    }

    try {
      const parts =
        new Intl.DateTimeFormat(
          "en-CA",
          {
            timeZone:
              timezone,

            year: "numeric",

            month: "2-digit",

            day: "2-digit",
          }
        ).formatToParts(
          date
        );

      const year =
        parts.find(
          (item) =>
            item.type ===
            "year"
        )?.value;

      const month =
        parts.find(
          (item) =>
            item.type ===
            "month"
        )?.value;

      const day =
        parts.find(
          (item) =>
            item.type ===
            "day"
        )?.value;

      if (
        !year ||
        !month ||
        !day
      ) {
        return "";
      }

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error(
        "DATE KEY ERROR:",
        error
      );

      return "";
    }
  };

  /*
  =========================================================
  FORMAT DATE
  =========================================================
  */

  const formatDate = (
    match
  ) => {
    const date =
      parseMatchDate(match);

    if (!date) {
      return "Date unavailable";
    }

    try {
      const timezone =
        match.timezone ||
        TIMEZONE;

      return new Intl.DateTimeFormat(
        "en-US",
        {
          timeZone:
            timezone,

          day: "numeric",

          month: "short",

          year: "numeric",

          hour: "numeric",

          minute: "2-digit",

          hour12: true,
        }
      ).format(date);
    } catch (error) {
      console.error(
        "FORMAT DATE ERROR:",
        error
      );

      return date.toLocaleString();
    }
  };

  /*
  =========================================================
  GAME PERIOD
  =========================================================
  */

  const getGamePeriod = (
    match
  ) => {
    const firstStatus =
      String(
        match?.firstStatus ||
          ""
      )
        .trim()
        .toLowerCase();

    const secondStatus =
      String(
        match?.secondStatus ||
          ""
      )
        .trim()
        .toLowerCase();

    /*
    -------------------------------------------------------
    DAILY
    -------------------------------------------------------
    */

    if (
      firstStatus ===
        "first" ||
      firstStatus ===
        "daily"
    ) {
      return "Daily";
    }

    /*
    -------------------------------------------------------
    NIGHT
    -------------------------------------------------------
    */

    if (
      secondStatus ===
        "second" ||
      secondStatus ===
        "night"
    ) {
      return "Night";
    }

    return "";
  };

  /*
  =========================================================
  COUNTDOWN
  =========================================================
  */

  const getTimeLeft = (
    match
  ) => {
    const date =
      parseMatchDate(match);

    if (!date) {
      return "Unknown";
    }

    let diff =
      date.getTime() -
      now;

    /*
    -------------------------------------------------------
    MATCH STARTED
    -------------------------------------------------------
    */

    if (diff <= 0) {
      return "Starting";
    }

    /*
    -------------------------------------------------------
    HOURS
    -------------------------------------------------------
    */

    const hours =
      Math.floor(
        diff /
          (
            1000 *
            60 *
            60
          )
      );

    diff -=
      hours *
      1000 *
      60 *
      60;

    /*
    -------------------------------------------------------
    MINUTES
    -------------------------------------------------------
    */

    const minutes =
      Math.floor(
        diff /
          (
            1000 *
            60
          )
      );

    diff -=
      minutes *
      1000 *
      60;

    /*
    -------------------------------------------------------
    SECONDS
    -------------------------------------------------------
    */

    const seconds =
      Math.floor(
        diff /
          1000
      );

    /*
    -------------------------------------------------------
    MORE THAN 1 HOUR
    -------------------------------------------------------
    */

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }

    /*
    -------------------------------------------------------
    LESS THAN 1 HOUR
    -------------------------------------------------------
    */

    return `${minutes}m ${seconds}s`;
  };

  /*
  =========================================================
  NORMALIZE STATUS
  =========================================================
  */

  const normalizeStatus = (
    status
  ) => {
    if (
      status === null ||
      status === undefined
    ) {
      return "";
    }

    return String(
      status
    )
      .trim()
      .toLowerCase();
  };

  /*
  =========================================================
  CHECK SPECIAL MATCH
  =========================================================

  MATCH MUST:

  1. status = OK
  2. be TODAY
  3. have more than 15 minutes remaining

  Daily/Night is OPTIONAL in this component.
  =========================================================
  */

  const isSpecialMatch = (
    match,
    currentTime
  ) => {
    /*
    -------------------------------------------------------
    STATUS
    -------------------------------------------------------
    */

    const status =
      normalizeStatus(
        match?.status
      );

    if (status !== "ok") {
      return false;
    }

    // Daily/Night is not required here.
    // Matches with status OK can appear even if both fields are empty.

    /*
    -------------------------------------------------------
    MATCH DATE
    -------------------------------------------------------
    */

    const matchDate =
      parseMatchDate(
        match
      );

    if (!matchDate) {
      return false;
    }

    /*
    -------------------------------------------------------
    FUTURE MATCHES — ANY DATE
    -------------------------------------------------------

    Matches are not restricted to today.
    Any match in the future can appear as long as
    it starts more than 15 minutes from now.
    */

    /*
    -------------------------------------------------------
    TIME UNTIL MATCH
    -------------------------------------------------------
    */

    const matchTime =
      matchDate.getTime();

    const timeUntilMatch =
      matchTime -
      currentTime;

    /*
    ONLY MORE THAN 15 MINUTES
    */

    return (
      timeUntilMatch >
      HIDE_BEFORE_MS
    );
  };

  /*
  =========================================================
  LOAD MATCHES
  =========================================================
  */

  const loadMatches =
    async () => {
      try {
        setLoading(true);

        const snapshot =
          await getDocs(
            collection(
              db,
              "matches"
            )
          );

        const allMatches =
          snapshot.docs.map(
            (
              docItem
            ) => ({
              id:
                docItem.id,

              ...docItem.data(),
            })
          );

        const currentTime =
          Date.now();

        /*
        -------------------------------------------------------
        FILTER + SORT
        -------------------------------------------------------
        */

        const specialMatches =
          allMatches
            .filter(
              (
                match
              ) =>
                isSpecialMatch(
                  match,
                  currentTime
                )
            )
            .sort(
              (
                a,
                b
              ) => {
                const dateA =
                  parseMatchDate(
                    a
                  );

                const dateB =
                  parseMatchDate(
                    b
                  );

                if (
                  !dateA &&
                  !dateB
                ) {
                  return 0;
                }

                if (!dateA) {
                  return 1;
                }

                if (!dateB) {
                  return -1;
                }

                return (
                  dateA.getTime() -
                  dateB.getTime()
                );
              }
            );

        setMatches(
          specialMatches
        );
      } catch (error) {
        console.error(
          "FAILED TO LOAD SPECIAL GAMES:",
          error
        );

        setMatches([]);
      } finally {
        setLoading(false);
      }
    };

  /*
  =========================================================
  INITIAL LOAD
  =========================================================
  */

  useEffect(() => {
    loadMatches();
  }, []);

  /*
  =========================================================
  REFRESH FIREBASE EVERY 60 SECONDS
  =========================================================
  */

  useEffect(() => {
    const refreshTimer =
      setInterval(
        () => {
          loadMatches();
        },
        60 * 1000
      );

    return () => {
      clearInterval(
        refreshTimer
      );
    };
  }, []);

  /*
  =========================================================
  LIVE FILTER
  =========================================================
  */

  const visibleMatches =
    useMemo(() => {
      return matches.filter(
        (
          match
        ) =>
          isSpecialMatch(
            match,
            now
          )
      );
    }, [
      matches,
      now,
    ]);

  /*
  =========================================================
  RESET SEE MORE
  =========================================================
  */

  useEffect(() => {
    setVisible(4);
  }, [
    visibleMatches.length,
  ]);

  /*
  =========================================================
  LOADING
  =========================================================
  */

  if (loading) {
    return (
      <div className="special-games">

        <div className="special-loading">

          <div className="loading-spinner" />

          <span>
            Loading today's games...
          </span>

        </div>

      </div>
    );
  }

  /*
  =========================================================
  MAIN
  =========================================================
  */

  return (
    <div className="special-games">

      {visibleMatches
        .slice(
          0,
          visible
        )
        .map(
          (
            match,
            index
          ) => {

            /*
            =================================================
            TEAM A
            =================================================
            */

            const teamA =
              match.A ||
              match.teamA ||
              match.homeTeam ||
              "Team A";

            /*
            =================================================
            TEAM B
            =================================================
            */

            const teamB =
              match.B ||
              match.teamB ||
              match.awayTeam ||
              "Team B";

            /*
            =================================================
            LEAGUE
            =================================================
            */

            const league =
              match.league ||
              match.competition ||
              "Special Game";

            /*
            =================================================
            MATCH ODDS
            =================================================
            */

            const matchOdds =
              match.matchOdds ||
              {};

            /*
            =================================================
            SCORE
            =================================================
            */

            const score =
              match.score ||
              match.matchOdds?.score ||
              match.correctScore ||
              match.correct_score ||
              match.correctScores?.[0]
                ?.score ||
              "0-0";

            /*
            =================================================
            SELECTED CORRECT SCORE
            =================================================
            */

            const selectedCorrectScore =
              Array.isArray(
                match.correctScores
              )
                ? match.correctScores.find(
                    (
                      item
                    ) =>
                      String(
                        item?.score
                      ) ===
                      String(
                        score
                      )
                  )
                : null;

            /*
            =================================================
            ODDS
            =================================================
            */

            const odds =
              match.odds ??
              matchOdds.odds ??
              match.percent ??
              selectedCorrectScore?.odds ??
              0;

            /*
            =================================================
            DYNAMIC QUANTITY
            =================================================

            Quantity sasa inakuwa
            dynamic.

            Example:

            1,184M
            1,192M
            1,201M
            1,209M
            ...
            1,900M

            Inaongezeka kwa dakika.
            =================================================
            */

            const quantity =
              getDynamicQuantity(
                match,
                now
              );

            /*
            =================================================
            COUNTDOWN
            =================================================
            */

            const countdown =
              getTimeLeft(
                match
              );

            /*
            =================================================
            PERIOD
            =================================================
            */

            const gamePeriod =
              getGamePeriod(
                match
              );

            /*
            =================================================
            NAVIGATION
            =================================================
            */

            const openMatch =
              () => {
                navigate(
                  `/betscores/${match.id}`,
                  {
                    state:
                      match,
                  }
                );
              };

            /*
            =================================================
            CARD
            =================================================
            */

            return (
              <div
                key={
                  match.id ||
                  index
                }
                className="match-card"
                role="button"
                tabIndex={0}
                onClick={
                  openMatch
                }
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                      "Enter" ||
                    event.key ===
                      " "
                  ) {
                    event.preventDefault();

                    openMatch();
                  }
                }}
              >

                {/* =========================================
                    LEFT QUANTITY
                ========================================== */}

                <div className="left-box">

                  <small>
                    Total Quantity
                  </small>

                  <h1>
                    {quantity.toLocaleString()}
                    M
                  </h1>

                </div>


                {/* =========================================
                    MATCH INFO
                ========================================== */}

                <div className="match-info">

                  {/* =======================================
                      DATE ROW
                  ======================================== */}

                  <div className="date-row">

                    <div className="date">

                      <span className="date-label">
                        Date
                      </span>

                      <span className="date-value">
                        {formatDate(
                          match
                        )}
                      </span>

                    </div>


                    {gamePeriod && (
                      <span
                        className={`session-badge ${
                          gamePeriod ===
                          "Daily"
                            ? "daily"
                            : "night"
                        }`}
                      >

                        {/* =================================
                            PERIOD ICON
                        ================================== */}

                        <span
                          className="period-svg"
                          aria-hidden="true"
                        >

                          {gamePeriod ===
                          "Daily" ? (
                            <svg
                              viewBox="0 0 24 24"
                              width="17"
                              height="17"
                            >

                              <circle
                                cx="12"
                                cy="12"
                                r="4"
                                fill="currentColor"
                              />

                              <path
                                d="M12 2V4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M12 20V22"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M2 12H4"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M20 12H22"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M4.9 4.9L6.3 6.3"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M17.7 17.7L19.1 19.1"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M17.7 6.3L19.1 4.9"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                              <path
                                d="M4.9 19.1L6.3 17.7"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />

                            </svg>
                          ) : (
                            <svg
                              viewBox="0 0 24 24"
                              width="17"
                              height="17"
                            >

                              <path
                                d="M20.5 14.8C19.2 15.4 17.8 15.7 16.3 15.7C11.2 15.7 7.1 11.6 7.1 6.5C7.1 5.1 7.4 3.7 8 2.5C4.7 4 2.5 7.3 2.5 11.1C2.5 16.8 7.2 21.5 12.9 21.5C16.7 21.5 20 19.3 21.5 16C21.2 16.4 20.9 16.6 20.5 16.8"
                                fill="currentColor"
                              />

                            </svg>
                          )}

                        </span>


                        <span className="period-text">
                          {gamePeriod}
                        </span>

                      </span>
                    )}

                  </div>


                  {/* =======================================
                      TEAMS
                  ======================================== */}

                  <div className="teams">

                    {/* TEAM A */}

                    <div className="team-name team-home">

                      <span className="team-text">
                        {teamA}
                      </span>

                    </div>


                    {/* VS */}

                    <div className="vs">

                      <span>
                        vs
                      </span>

                    </div>


                    {/* TEAM B */}

                    <div className="team-name team-away">

                      <span className="team-text">
                        {teamB}
                      </span>

                    </div>

                  </div>


                  {/* =======================================
                      BOTTOM ROW
                  ======================================== */}

                  <div className="bottom-row">

                    {/* SCORE */}

                    <span className="score-box">

                      <span className="score-label">
                        Score
                      </span>

                      <strong>
                        {score}
                      </strong>

                    </span>


                    {/* ODDS */}

                    <span className="odds-box">

                      @
                      {Number(
                        odds
                      ).toFixed(2)}
                      %

                    </span>


                    {/* COUNTDOWN */}

                    <span
                      className={`starts ${
                        countdown ===
                        "Starting"
                          ? "starting"
                          : ""
                      }`}
                    >
                      {countdown}
                    </span>


                    {/* LEAGUE */}

                    <div className="league">

                      <span className="league-icon">

                        <svg
                          viewBox="0 0 24 24"
                          width="15"
                          height="15"
                        >

                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />

                          <path
                            d="M12 7l2 3h3l-2.5 2 1 3-3.5-2-3.5 2 1-3L7 10h3z"
                            fill="currentColor"
                          />

                        </svg>

                      </span>


                      <span>
                        {league}
                      </span>

                    </div>

                  </div>

                </div>

              </div>
            );
          }
        )}


      {/* =====================================================
          SEE MORE
      ====================================================== */}

      {visible <
        visibleMatches.length && (
        <button
          type="button"
          className="more-btn"
          onClick={() =>
            setVisible(
              (
                value
              ) =>
                value + 4
            )
          }
        >

          <span>
            See More
          </span>

          <span className="more-arrow">
            ↓
          </span>

        </button>
      )}


      {/* =====================================================
          EMPTY
      ====================================================== */}

      {visibleMatches.length ===
        0 && (
        <div className="special-empty">

          <div className="empty-icon">

            <svg
              viewBox="0 0 64 64"
              width="50"
              height="50"
            >

              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              />

              <path
                d="M32 15L40 21L37 31H27L24 21Z"
                fill="currentColor"
              />

              <path
                d="M27 31L17 37L21 47L31 43"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              />

              <path
                d="M37 31L47 37L43 47L33 43"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              />

            </svg>

          </div>


          <strong>
            No GrowthPlan Available
          </strong>


          <small>
            OK games from any date starting more than
            15 minutes from now are shown.
          </small>

        </div>
      )}

    </div>
  );
}